import { ReactNode } from 'react';
import { PiCaretLeftBold, PiCaretRightBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';

export type TablePaginationMeta = {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from?: number | null;
    to?: number | null;
};

type Props = {
    meta: TablePaginationMeta;
    onPageChange: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
    perPageOptions?: number[];
    itemLabel?: string;
    className?: string;
};

const DEFAULT_PER_PAGE_OPTIONS = [10, 25, 50, 100];

export const TABLE_PER_PAGE_OPTIONS = DEFAULT_PER_PAGE_OPTIONS;

export default function TablePagination({
    meta,
    onPageChange,
    onPerPageChange,
    perPageOptions = DEFAULT_PER_PAGE_OPTIONS,
    itemLabel = 'resultado',
    className,
}: Props) {
    const { current_page, last_page, per_page, total } = meta;
    const from = meta.from ?? (total === 0 ? 0 : (current_page - 1) * per_page + 1);
    const to = meta.to ?? (total === 0 ? 0 : Math.min(current_page * per_page, total));
    const plural = total !== 1 ? 's' : '';

    return (
        <div
            className={cn(
                'flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 px-4 py-3 dark:border-gray-700/50',
                className,
            )}
        >
            <div className="flex flex-wrap items-center gap-3">
                <p className="text-xs text-gray-500 dark:text-zinc-500">
                    {total === 0
                        ? `Nenhum ${itemLabel}`
                        : `Exibindo ${from}–${to} de ${total} ${itemLabel}${plural}`}
                </p>

                {onPerPageChange && (
                    <div className="flex items-center gap-1.5">
                        <span className="text-xs text-gray-400 dark:text-zinc-600">Linhas por página:</span>
                        <select
                            value={per_page}
                            onChange={(e) => {
                                onPerPageChange(Number(e.target.value));
                                onPageChange(1);
                            }}
                            aria-label="Linhas por página"
                            className="h-8 min-w-[4.5rem] rounded-md border border-gray-200 bg-white px-2.5 pe-8 text-xs font-semibold text-gray-700 outline-none transition focus:border-primary focus:ring-1 focus:ring-primary/30 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-300 dark:focus:border-amber-500/60"
                        >
                            {perPageOptions.map((n) => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {last_page > 1 && (
                <Paginator
                    currentPage={current_page}
                    lastPage={last_page}
                    onPageChange={onPageChange}
                />
            )}
        </div>
    );
}

function Paginator({
    currentPage,
    lastPage,
    onPageChange,
}: {
    currentPage: number;
    lastPage: number;
    onPageChange: (page: number) => void;
}) {
    const pages = buildPageList(currentPage, lastPage);

    return (
        <nav className="flex items-center gap-1" aria-label="Paginação">
            <PageBtn
                disabled={currentPage <= 1}
                onClick={() => onPageChange(currentPage - 1)}
                title="Página anterior"
            >
                <PiCaretLeftBold className="size-3.5" />
            </PageBtn>

            {pages.map((p, i) =>
                p === '...' ? (
                    <span key={`ellipsis-${i}`} className="px-1 text-xs text-gray-400 dark:text-zinc-600">…</span>
                ) : (
                    <PageBtn
                        key={p}
                        active={p === currentPage}
                        onClick={() => onPageChange(p as number)}
                    >
                        {p}
                    </PageBtn>
                ),
            )}

            <PageBtn
                disabled={currentPage >= lastPage}
                onClick={() => onPageChange(currentPage + 1)}
                title="Próxima página"
            >
                <PiCaretRightBold className="size-3.5" />
            </PageBtn>
        </nav>
    );
}

function PageBtn({
    children,
    active,
    disabled,
    onClick,
    title,
}: {
    children: ReactNode;
    active?: boolean;
    disabled?: boolean;
    onClick: () => void;
    title?: string;
}) {
    return (
        <button
            type="button"
            title={title}
            disabled={disabled}
            onClick={onClick}
            className={cn(
                'flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-xs font-bold transition',
                active
                    ? 'bg-primary text-primary-foreground shadow-sm'
                    : 'border border-gray-200 bg-white text-gray-600 hover:border-primary/40 hover:text-primary dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-400 dark:hover:border-primary/50 dark:hover:text-primary',
                disabled && 'cursor-not-allowed opacity-40',
            )}
        >
            {children}
        </button>
    );
}

function buildPageList(current: number, last: number): (number | '...')[] {
    if (last <= 7) {
        return Array.from({ length: last }, (_, i) => i + 1);
    }

    const pages: (number | '...')[] = [1];

    if (current > 3) {
        pages.push('...');
    }

    const start = Math.max(2, current - 1);
    const end = Math.min(last - 1, current + 1);
    for (let i = start; i <= end; i++) {
        pages.push(i);
    }

    if (current < last - 2) {
        pages.push('...');
    }

    pages.push(last);

    return pages;
}
